/**
 * Dados SIMULADOS do modulo de disparos (RNF19) -- NAO E PRODUCAO.
 *
 * Como os numeros nascem:
 *  1. Nenhum Math.random. Tudo deriva de um hash estavel do id da empresa, da
 *     posicao na lista e dos dias da janela: a mesma tela mostra sempre o
 *     mesmo conjunto, em qualquer maquina.
 *  2. Volume proporcional aos dias da janela (12 envios e 10 campanhas de
 *     e-mail a cada 30 dias, para uma empresa de peso 100). Janela nula
 *     ("desde o inicio") vale 30 dias e nao tem periodo anterior.
 *  3. SO DEPOIS DO FATO: os publicos sao abandonaram sem pagar, clicaram sem
 *     pagar, pagamento recusado, responderam sem fechar e expirados D+15/30/60.
 *     Modelos e copies de e-mail falam com quem ja saiu -- nenhum lembrete
 *     antes de vencer, nenhuma "novidade para toda a base".
 *  4. RETORNO segue o ADR-001: pagamento em ate 7 dias com o envio como
 *     ultimo contato, pelo valor da oferta do catalogo. Enquanto a janela de
 *     7 dias esta aberta, a taxa de pagamento aparece menor (75%). A
 *     comissao usa o `feeBps` da empresa JA COM o ajuste da administracao
 *     (`cf.empresas.ajustes`), para bater com o cartao "Sua comissao".
 *  5. Publico, remetentes, vendedoras, saude do numero e opt-out sao RETRATO
 *     de agora. So envios e campanhas respeitam o periodo do cabecalho.
 *  6. A empresa em implantacao (`onboarding`) nasce com a Evolution
 *     desconectada, sem historico (sem estimativa de retorno) e sem envio
 *     nenhum: e o estado que a tela precisa saber mostrar antes do primeiro
 *     cliente real.
 *  7. Copy e nomes sao inventados e usam as ofertas do catalogo. Sem cupom:
 *     o produto nao tem cupom em camada nenhuma.
 */

import { AGORA_SIMULADO, menosMinutos } from '../../lib/api';
import { lerAjustesDeEmpresas } from '../../lib/companyContext';
import { EMPRESAS, empresaPorId, equipeDaEmpresa, ofertaCiclica, ofertasDaEmpresa } from '../../lib/mock/catalogo';
import { rangeDays } from '../../lib/period';
import type { CompanySummary, PeriodRange } from '../../types';
import { tarifaPorMensagemCents } from './estimativa';
import type {
  Broadcast,
  BroadcastStatus,
  BroadcastTotals,
  BroadcastsData,
  EmailAutomation,
  EmailCampaign,
  EmailCampaignStatus,
  EmailCampaignsData,
  EmailDailyPoint,
  EmailDomainHealth,
  EmailTotals,
  EmailUnsubscribe,
  MessageTemplateSummary,
  NumberHealth,
  Opportunity,
  OptOutContact,
  OptOutReason,
  PeriodWindow,
  Segment,
  SegmentFamily,
  SegmentKey,
  SellerOption,
  SenderKind,
  SenderOption,
  WhatsAppContext,
  WhatsAppTariffs,
} from './types';
import { SEGMENT_LABEL } from './vocabulario';

const DIA_MS = 86_400_000;
const MINUTOS_POR_DIA = 1_440;
const DIAS_PADRAO = 30;
const DIAS_MAXIMO = 365;
const ENVIOS_BASE_30D = 8;
const CAMPANHAS_EMAIL_BASE_30D = 10;
const MAXIMO_ENVIOS = 60;
const MAXIMO_CAMPANHAS = 40;
const AMOSTRA_OPTOUT = 6;
const AMOSTRA_DESCADASTRO = 8;
const JANELA_ATRIBUICAO_DIAS = 7;

/** Tarifas de EXEMPLO (mesmas da Central: R$ 0,35 por conversa de marketing). */
export const TARIFAS_WHATSAPP: WhatsAppTariffs = {
  officialUtilityCents: 8,
  officialMarketingCents: 35,
  evolutionMonthlyCents: 8_900,
  source: 'exemplo',
};

/** R$ 2,90 por mil e-mails aceitos (exemplo enquanto o provedor nao e escolhido). */
export const TARIFA_EMAIL_POR_MIL_CENTS = 290;

// -----------------------------------------------------------------------------
// Aritmetica deterministica
// -----------------------------------------------------------------------------

/** Hash pequeno e estavel do id: mesmo id, mesmo numero, em qualquer maquina. */
export function hashSimulado(texto: string): number {
  let h = 7;
  for (let i = 0; i < texto.length; i += 1) {
    h = (h * 31 + texto.charCodeAt(i)) % 10_007;
  }
  return h;
}

/** Dias cobertos pela janela, com teto; janela nula (desde o inicio) vale 30. */
export function diasDaJanela(range: PeriodRange): number {
  const dias = rangeDays(range);
  if (dias === null) {
    return DIAS_PADRAO;
  }
  return Math.max(1, Math.min(DIAS_MAXIMO, dias));
}

/** `base * bps / 10000`, arredondado para inteiro. Substitui qualquer `* 0.21`. */
function proporcao(base: number, bps: number): number {
  return Math.round((base * bps) / 10_000);
}

function limitar(valor: number, minimo: number, maximo: number): number {
  return Math.max(minimo, Math.min(maximo, valor));
}

/** Peso da empresa em porcento (60..160): taxa maior e hash maior = base maior. */
export function pesoDaEmpresa(empresa: CompanySummary): number {
  return limitar(Math.round(empresa.feeBps / 15) + (hashSimulado(empresa.id) % 60), 60, 160);
}

/** Escala uma base de 30 dias / peso 100 para a janela e a empresa. */
function escalar(base: number, dias: number, peso: number): number {
  return Math.round((base * dias * peso) / (DIAS_PADRAO * 100));
}

/** Celular ficticio no padrao do guia: +55 11 9999x-xxxx. */
function celularFicticio(semente: number): string {
  return `+55119999${String(semente % 100_000).padStart(5, '0')}`;
}

function inicioDoDia(iso: string): string {
  return iso.slice(0, 10);
}

/** "24/09" a partir do ISO (UTC): igual em qualquer fuso, como pede o mock. */
function diaMes(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/** Comissao da empresa com o ajuste feito pela administracao na previa. */
function comissaoBps(empresa: CompanySummary): number {
  const patch = lerAjustesDeEmpresas()[empresa.id];
  return patch !== undefined && typeof patch.feeBps === 'number' && Number.isInteger(patch.feeBps) ? patch.feeBps : empresa.feeBps;
}

function janelaAnterior(range: PeriodRange): PeriodWindow | null {
  if (range.from === null || range.to === null) {
    return null;
  }
  const inicio = new Date(range.from).getTime();
  const fim = new Date(range.to).getTime();
  return { start: new Date(inicio - (fim - inicio)).toISOString(), end: range.from };
}

/** Empresa fora do catalogo (sessao real sem mock): o minimo para a tela nao quebrar. */
function empresaFallback(companyId: string): CompanySummary {
  return {
    id: companyId,
    name: 'Sua empresa',
    slug: 'sua-empresa',
    segment: '',
    logoInitials: 'SE',
    logoTone: 1,
    logoUrl: null,
    feeBps: 1_500,
    billingModel: 'a_definir',
    status: 'onboarding',
    providers: [],
    funnels: ['carrinho_abandonado'],
    contactName: '',
    contactEmail: '',
    createdAt: menosMinutos(MINUTOS_POR_DIA),
  };
}

/** Preco de uma oferta da familia (avulsa para carrinho/comercial, assinatura para expirados). */
function ticketDaFamilia(empresa: CompanySummary, familia: SegmentFamily, indice: number | null): number {
  const ativas = ofertasDaEmpresa(empresa.id).filter((o) => o.active);
  const daFamilia = ativas.filter((o) => (familia === 'expirados' ? o.recurring : !o.recurring));
  const lista = daFamilia.length > 0 ? daFamilia : ativas;
  if (lista.length === 0) {
    return 19_700;
  }
  if (indice === null) {
    return Math.round(lista.reduce((acc, o) => acc + o.priceCents, 0) / lista.length);
  }
  return lista[indice % lista.length]?.priceCents ?? 19_700;
}

// -----------------------------------------------------------------------------
// Modelos (resumo) -- todos para quem ja saiu
// -----------------------------------------------------------------------------

const RODAPE_SAIR = 'Responda SAIR para não receber mais mensagens.';

export const MODELOS_WHATSAPP: readonly MessageTemplateSummary[] = [
  {
    id: 'SIMULADO-modelo-001',
    name: 'Lembrete de carrinho parado',
    channel: 'whatsapp',
    category: 'utility',
    sector: 'funil',
    approval: 'aprovado',
    fits: ['carrinho'],
    variables: ['primeiro_nome', 'produto'],
    buttons: [
      { label: 'Tirar dúvida', kind: 'reply' },
      { label: 'Concluir compra', kind: 'link' },
    ],
    body: 'Oi, {primeiro_nome}! Vi que o seu {produto} ficou parado no checkout. Está tudo salvo do jeito que você deixou — quer terminar agora?\nSe travou alguma coisa, me responde por aqui que a gente resolve.',
    footer: RODAPE_SAIR,
    updatedAt: menosMinutos(3 * MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-002',
    name: 'Reserva encerrando',
    channel: 'whatsapp',
    category: 'utility',
    sector: 'funil',
    approval: 'aprovado',
    fits: ['carrinho'],
    variables: ['primeiro_nome', 'produto'],
    buttons: [{ label: 'Concluir agora', kind: 'link' }],
    body: 'Último aviso, {primeiro_nome}: a reserva do seu {produto} encerra hoje.\nSe ainda fizer sentido, é só concluir pelo botão. Se não, tudo bem — não vou te lembrar de novo.',
    footer: RODAPE_SAIR,
    updatedAt: menosMinutos(9 * MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-003',
    name: 'Pix venceu: gere outro em um toque',
    channel: 'whatsapp',
    category: 'utility',
    sector: 'suporte',
    approval: 'aprovado',
    fits: ['carrinho'],
    variables: ['primeiro_nome', 'produto'],
    buttons: [
      { label: 'Gerar novo Pix', kind: 'link' },
      { label: 'Pagar de outro jeito', kind: 'reply' },
    ],
    body: 'Oi, {primeiro_nome}! O Pix do seu {produto} venceu antes do pagamento — acontece. Se ainda quiser, o botão gera um novo na hora, com o mesmo valor.\nPrefere cartão ou boleto? Me responde aqui.',
    footer: RODAPE_SAIR,
    updatedAt: menosMinutos(4 * MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-004',
    name: 'Cartão recusado: outra forma de pagar',
    channel: 'whatsapp',
    category: 'utility',
    sector: 'suporte',
    approval: 'aprovado',
    fits: ['carrinho'],
    variables: ['primeiro_nome', 'produto'],
    buttons: [
      { label: 'Tentar outro cartão', kind: 'link' },
      { label: 'Falar com alguém', kind: 'reply' },
    ],
    body: 'Oi, {primeiro_nome}. O pagamento do seu {produto} não passou no cartão — quase sempre é limite ou bloqueio do banco.\nDá pra tentar outro cartão ou Pix pelo botão. Se preferir, me responde que eu ajudo.',
    footer: RODAPE_SAIR,
    updatedAt: menosMinutos(11 * MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-005',
    name: 'Vendedora retoma a conversa',
    channel: 'whatsapp',
    category: 'utility',
    sector: 'comercial',
    approval: 'texto_livre',
    fits: ['comercial'],
    variables: ['primeiro_nome', 'produto', 'vendedora'],
    buttons: [{ label: 'Pode me chamar', kind: 'reply' }],
    body: 'Oi, {primeiro_nome}! Aqui é a {vendedora}, do comercial. A gente conversou sobre o {produto} e ficou uma dúvida no ar.\nPosso te ajudar a terminar por aqui mesmo?',
    footer: null,
    updatedAt: menosMinutos(6 * MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-006',
    name: 'Formas de pagamento para quem não fechou',
    channel: 'whatsapp',
    category: 'marketing',
    sector: 'comercial',
    approval: 'em_analise',
    fits: ['comercial', 'carrinho'],
    variables: ['primeiro_nome', 'produto'],
    buttons: [{ label: 'Quero ver as opções', kind: 'reply' }],
    body: '{primeiro_nome}, vi que faltou pouco para você concluir o {produto}. Separei as formas de pagamento que costumam caber melhor: Pix, cartão em parcelas ou boleto.\nQuer que eu te mande os detalhes?',
    footer: RODAPE_SAIR,
    updatedAt: menosMinutos(MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-007',
    name: 'Assinatura vencida (D+15)',
    channel: 'whatsapp',
    category: 'utility',
    sector: 'funil',
    approval: 'aprovado',
    fits: ['expirados'],
    variables: ['primeiro_nome', 'plano'],
    buttons: [
      { label: 'Reativar acesso', kind: 'link' },
      { label: 'Parei por um motivo', kind: 'reply' },
    ],
    body: 'Oi, {primeiro_nome}! Faz 15 dias que o seu acesso ao {plano} venceu. Tudo o que você fez continua salvo.\nSe quiser voltar, é um toque no botão.',
    footer: RODAPE_SAIR,
    updatedAt: menosMinutos(8 * MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-008',
    name: 'Volta da assinatura (D+30)',
    channel: 'whatsapp',
    category: 'marketing',
    sector: 'funil',
    approval: 'aprovado',
    fits: ['expirados'],
    variables: ['primeiro_nome', 'plano'],
    buttons: [
      { label: 'Reativar acesso', kind: 'link' },
      { label: 'Falar com a equipe', kind: 'reply' },
    ],
    body: 'Oi, {primeiro_nome}! Faz 30 dias que o seu acesso ao {plano} está parado. Seu progresso continua salvo.\nQuer retomar de onde parou?',
    footer: RODAPE_SAIR,
    updatedAt: menosMinutos(14 * MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-009',
    name: 'Último contato do plano (D+60)',
    channel: 'whatsapp',
    category: 'marketing',
    sector: 'funil',
    approval: 'aprovado',
    fits: ['expirados'],
    variables: ['primeiro_nome', 'plano'],
    buttons: [
      { label: 'Quero voltar', kind: 'link' },
      { label: 'Não tenho interesse', kind: 'reply' },
    ],
    body: 'Oi, {primeiro_nome}. Faz dois meses que o seu {plano} está parado e este é o meu último contato sobre isso.\nSe quiser retomar, o botão reativa com tudo salvo. Se não, tudo bem — não vou te lembrar de novo.',
    footer: RODAPE_SAIR,
    updatedAt: menosMinutos(20 * MINUTOS_POR_DIA),
  },
  {
    id: 'SIMULADO-modelo-010',
    name: 'Dúvida no checkout',
    channel: 'whatsapp',
    category: 'utility',
    sector: 'suporte',
    approval: 'texto_livre',
    fits: ['carrinho', 'comercial'],
    variables: ['primeiro_nome', 'produto'],
    buttons: [],
    body: 'Oi, {primeiro_nome}! Aqui é do comercial. Vi que você chegou até o pagamento do {produto} e parou. Ficou alguma dúvida sobre acesso, garantia ou forma de pagamento?\nMe responde por aqui que eu te explico.',
    footer: null,
    updatedAt: menosMinutos(2 * MINUTOS_POR_DIA),
  },
];

export function simularModelos(): readonly MessageTemplateSummary[] {
  return MODELOS_WHATSAPP;
}

// -----------------------------------------------------------------------------
// Publico, remetentes, vendedoras e saude do numero (retrato de agora)
// -----------------------------------------------------------------------------

interface SegmentoBase {
  readonly key: SegmentKey;
  readonly family: SegmentFamily;
  readonly fact: string;
  readonly description: string;
  /** Contatos para peso 100. */
  readonly base: number;
  /** Taxa de pagamento historica (bps sobre enviadas) e sua variacao. */
  readonly pagamentoBps: number;
  readonly variacaoBps: number;
  readonly respostaBps: number;
}

const SEGMENTOS_BASE: readonly SegmentoBase[] = [
  {
    key: 'abandonaram_sem_pagar',
    family: 'carrinho',
    fact: 'Checkout abandonado há 48 h ou mais; a régua automática terminou sem pagamento.',
    description: 'Ninguém mais fala com eles: o motor já encerrou os toques.',
    base: 180,
    pagamentoBps: 160,
    variacaoBps: 130,
    respostaBps: 700,
  },
  {
    key: 'clicaram_sem_pagar',
    family: 'carrinho',
    fact: 'Tocaram no botão de um toque da régua, abriram o checkout e não pagaram.',
    description: 'Intenção alta: chegaram a ver a tela de pagamento.',
    base: 64,
    pagamentoBps: 390,
    variacaoBps: 200,
    respostaBps: 900,
  },
  {
    key: 'pagamento_recusado',
    family: 'carrinho',
    fact: 'Pix ou boleto venceu, ou o cartão foi recusado, sem nova tentativa.',
    description: 'Quase compraram: costuma ser limite do cartão ou falta de outra forma de pagar.',
    base: 38,
    pagamentoBps: 520,
    variacaoBps: 250,
    respostaBps: 1_100,
  },
  {
    key: 'responderam_sem_fechar',
    family: 'comercial',
    fact: 'Responderam a um toque, caíram com a vendedora e não pagaram em 7 dias.',
    description: 'A conversa esfriou: uma retomada pelo mesmo número costuma bastar.',
    base: 42,
    pagamentoBps: 650,
    variacaoBps: 300,
    respostaBps: 2_600,
  },
  {
    key: 'expirados_d15',
    family: 'expirados',
    fact: 'Assinatura vencida há 15 a 29 dias, sem renovação.',
    description: 'Ainda lembram do produto: o progresso salvo é o argumento.',
    base: 70,
    pagamentoBps: 210,
    variacaoBps: 100,
    respostaBps: 800,
  },
  {
    key: 'expirados_d30',
    family: 'expirados',
    fact: 'Assinatura vencida há 30 a 59 dias, sem renovação.',
    description: 'Retomada de onde parou, sem pressa.',
    base: 90,
    pagamentoBps: 130,
    variacaoBps: 90,
    respostaBps: 600,
  },
  {
    key: 'expirados_d60',
    family: 'expirados',
    fact: 'Assinatura vencida há 60 dias ou mais, sem renovação.',
    description: 'Último contato: menos retorno, mas nenhum outro canal fala com eles.',
    base: 120,
    pagamentoBps: 70,
    variacaoBps: 60,
    respostaBps: 400,
  },
];

export function segmentosDaEmpresa(empresa: CompanySummary): readonly Segment[] {
  const h = hashSimulado(empresa.id);
  const peso = pesoDaEmpresa(empresa);
  const temExpirados = empresa.funnels.includes('expirados');
  const implantando = empresa.status === 'onboarding';
  return SEGMENTOS_BASE.map((s, i) => {
    const count = s.family === 'expirados' && !temExpirados ? 0 : Math.round((s.base * peso) / 100) + ((h * (i + 1)) % 37);
    const hs = hashSimulado(`${empresa.id}:${s.key}`);
    const enviadas = 400 + (hs % 1_400);
    const pagos = proporcao(enviadas, s.pagamentoBps + (hs % s.variacaoBps));
    return {
      key: s.key,
      family: s.family,
      label: SEGMENT_LABEL[s.key],
      fact: s.fact,
      description: s.description,
      estimatedCount: count,
      recentlyContactedCount: count === 0 ? 0 : proporcao(count, 600 + (hs % 1_400)),
      stalledAmountCents: count * ticketDaFamilia(empresa, s.family, null),
      benchmark:
        implantando || count === 0
          ? null
          : {
              broadcasts: 2 + (hs % 7),
              sent: enviadas,
              replied: proporcao(enviadas, s.respostaBps + (hs % 500)),
              paid: pagos,
              recoveredAmountCents: pagos * ticketDaFamilia(empresa, s.family, null),
            },
    };
  });
}

export function remetentesDaEmpresa(empresa: CompanySummary): readonly SenderOption[] {
  const h = hashSimulado(empresa.id);
  const implantando = empresa.status === 'onboarding';
  return [
    {
      id: `${empresa.id}:evolution`,
      kind: 'evolution',
      label: `Comercial · Evolution (cf-${empresa.slug})`,
      phoneE164: celularFicticio(h),
      connected: !implantando,
    },
    {
      id: `${empresa.id}:oficial`,
      kind: 'oficial',
      label: 'Comercial · API oficial da Meta',
      phoneE164: celularFicticio(h * 7 + 311),
      connected: !implantando && h % 3 !== 1,
    },
  ];
}

export function vendedorasDaEmpresa(empresa: CompanySummary): readonly SellerOption[] {
  return equipeDaEmpresa(empresa.id)
    .filter((m) => m.role === 'comercial')
    .map((m, i) => ({ id: m.id, name: m.name, openChats: (hashSimulado(m.id) + i * 3) % 9 }));
}

/**
 * Opt-out de 7 dias do numero do comercial nas empresas do catalogo: fixo para
 * a previa mostrar os tres estados (saudavel, atencao e alerta acima de 3%).
 */
const OPTOUT_7D_DO_COMERCIAL: Readonly<Record<string, number>> = {
  'SIMULADO-empresa-demo': 82,
  'SIMULADO-empresa-fit': 318,
  'SIMULADO-empresa-dev': 180,
};

function saudeDosNumeros(empresa: CompanySummary, senders: readonly SenderOption[]): readonly NumberHealth[] {
  const h = hashSimulado(empresa.id);
  const diasConectado = Math.max(0, Math.floor((AGORA_SIMULADO.getTime() - new Date(empresa.createdAt).getTime()) / DIA_MS));
  return senders.map((s, i) => {
    const fixo = s.kind === 'evolution' ? OPTOUT_7D_DO_COMERCIAL[empresa.id] : undefined;
    const optOut = !s.connected ? 0 : fixo ?? 60 + ((h * (i + 2)) % 340);
    return {
      senderId: s.id,
      quality: optOut < 150 ? 'alta' : optOut < 300 ? 'media' : 'baixa',
      dailyLimit: s.kind === 'oficial' ? (h % 2 === 0 ? 1_000 : 10_000) : null,
      sentToday: s.connected ? (h * (i + 5)) % 420 : 0,
      optOutRate7dBps: optOut,
      blockRate7dBps: s.connected ? 8 + ((h + i * 13) % 90) : 0,
      connectedDays: s.connected ? diasConectado : 0,
    };
  });
}

function exemploDeVariaveis(empresa: CompanySummary, sellers: readonly SellerOption[]): Readonly<Record<string, string>> {
  const ativas = ofertasDaEmpresa(empresa.id).filter((o) => o.active);
  const avulsa = ativas.find((o) => !o.recurring) ?? ativas[0];
  const plano = ativas.find((o) => o.recurring) ?? avulsa;
  const vendedora = sellers[0]?.name.split(' ')[0] ?? 'Equipe';
  return {
    primeiro_nome: 'Mariana',
    produto: avulsa?.name ?? 'seu produto',
    plano: plano?.name ?? 'seu plano',
    vendedora,
    link: `${empresa.slug}.exemplo/c/SIMULADO`,
  };
}

const MOTIVOS_OPTOUT: readonly OptOutReason[] = ['pediu_sair', 'pediu_sair', 'bloqueou_numero', 'manual'];

function optOutsDaEmpresa(empresa: CompanySummary): { readonly amostra: readonly OptOutContact[]; readonly total: number } {
  const h = hashSimulado(empresa.id);
  const peso = pesoDaEmpresa(empresa);
  const total = empresa.status === 'onboarding' ? 0 : limitar(Math.round(((18 + (h % 40)) * peso) / 100), 3, 80);
  const amostra = Array.from({ length: Math.min(AMOSTRA_OPTOUT, total) }, (_, i) => ({
    id: `SIMULADO-optout-${empresa.slug}-${String(i + 1).padStart(3, '0')}`,
    companyId: empresa.id,
    phoneE164: celularFicticio(h * 13 + i * 977),
    reason: MOTIVOS_OPTOUT[(h + i) % MOTIVOS_OPTOUT.length] ?? 'pediu_sair',
    at: menosMinutos(60 * (3 + i * 29) + (h % 50)),
  }));
  return { amostra, total };
}

export function simularContextoWhatsApp(companyId: string): WhatsAppContext {
  const empresa = empresaPorId(companyId) ?? empresaFallback(companyId);
  const senders = remetentesDaEmpresa(empresa);
  const sellers = vendedorasDaEmpresa(empresa);
  const optOuts = optOutsDaEmpresa(empresa);
  return {
    companyId,
    senders,
    segments: segmentosDaEmpresa(empresa),
    sellers,
    health: saudeDosNumeros(empresa, senders),
    optOuts: optOuts.amostra,
    optOutTotal: optOuts.total,
    tariffs: TARIFAS_WHATSAPP,
    sample: exemploDeVariaveis(empresa, sellers),
  };
}

// -----------------------------------------------------------------------------
// Envios em massa
// -----------------------------------------------------------------------------

const NOME_POR_SEGMENTO: Readonly<Record<SegmentKey, string>> = {
  abandonaram_sem_pagar: 'Carrinhos parados',
  clicaram_sem_pagar: 'Clicaram e não pagaram',
  pagamento_recusado: 'Pix vencido e cartão recusado',
  responderam_sem_fechar: 'Retomada do comercial',
  expirados_d15: 'Volta dos expirados D+15',
  expirados_d30: 'Volta dos expirados D+30',
  expirados_d60: 'Último aviso D+60',
};

function statusDoEnvio(posicao: number, dias: number): BroadcastStatus {
  if (posicao === 0 && dias >= 1) {
    return 'em_andamento';
  }
  if (posicao === 1) {
    return 'agendada';
  }
  if (posicao % 7 === 4) {
    return 'pausada';
  }
  return 'concluida';
}

function remetenteDoEnvio(modelo: MessageTemplateSummary, posicao: number): SenderKind {
  if (modelo.approval !== 'aprovado') {
    return 'evolution';
  }
  return posicao % 3 === 0 ? 'evolution' : 'oficial';
}

function enviosDaEmpresa(empresa: CompanySummary, dias: number, variante: 'atual' | 'anterior'): readonly Broadcast[] {
  const anterior = variante === 'anterior';
  // `hBase` escolhe publico e modelo (o mesmo mix nos dois periodos); `h` so muda o ruido dos numeros.
  const hBase = hashSimulado(empresa.id);
  const h = anterior ? hashSimulado(`${empresa.id}:anterior`) : hBase;
  const peso = pesoDaEmpresa(empresa);
  const fee = comissaoBps(empresa);
  const implantando = empresa.status === 'onboarding';
  // Empresa em implantacao ainda nao disparou (numero desconectado): a tela mostra o vazio que ensina.
  // O periodo anterior teve um ou dois envios a menos -- a operacao esta crescendo, e o delta mostra isso.
  const quantidadeAtual = implantando ? 0 : limitar(escalar(ENVIOS_BASE_30D, dias, peso), 2, MAXIMO_ENVIOS);
  const quantidade = anterior ? (implantando ? 0 : Math.max(1, quantidadeAtual - 2 + (h % 2))) : quantidadeAtual;
  const segmentos = segmentosDaEmpresa(empresa).filter((s) => s.estimatedCount > 0);
  const vendedoras = vendedorasDaEmpresa(empresa);
  const deslocamento = anterior ? dias * MINUTOS_POR_DIA : 0;

  return Array.from({ length: quantidade }, (_, i): Broadcast => {
    const segmento = segmentos.length === 0 ? null : segmentos[(hBase + i) % segmentos.length] ?? null;
    const candidatos = segmento === null ? MODELOS_WHATSAPP : MODELOS_WHATSAPP.filter((m) => m.fits.includes(segmento.family));
    const lista = candidatos.length > 0 ? candidatos : MODELOS_WHATSAPP;
    const modelo = lista[(hBase + i * 3) % lista.length];
    if (modelo === undefined) {
      throw new Error('catalogo de modelos vazio');
    }
    const viaCsv = i % 5 === 3;
    // No periodo anterior, o que estava em andamento ou agendado ja terminou; pausado que nao foi retomado continua pausado.
    const statusBase = statusDoEnvio(i, dias);
    const status: BroadcastStatus = anterior ? (statusBase === 'pausada' ? 'pausada' : 'concluida') : statusBase;
    const pausadoPorOptOut = status === 'pausada' && i % 2 === 0;
    const senderKind = remetenteDoEnvio(modelo, i);
    const minutosAtras = deslocamento + Math.round(((i + 0.5) * dias * MINUTOS_POR_DIA) / quantidade) + (h % 90);
    const createdAt = status === 'agendada' ? menosMinutos(40 + (h % 200)) : menosMinutos(minutosAtras);
    // O envio e do tamanho do publico (55% a 95% dele, depois de tirar quem recebeu ha pouco); lista importada, 120 a 400.
    const total = viaCsv || segmento === null ? 120 + ((h * (i + 3)) % 280) : Math.max(20, proporcao(segmento.estimatedCount, 5_500 + ((h * (i + 3)) % 4_000)));
    const failed = status === 'agendada' ? 0 : proporcao(total, 150 + ((h + i * 7) % 250));
    // Pausado pela trava: o opt-out acabou de passar de 3% do que saiu, como aconteceria de verdade.
    const optOut = status === 'agendada' ? 0 : pausadoPorOptOut ? proporcao(proporcao(total, 3_100), 330 + ((h + i) % 80)) : proporcao(total, 30 + ((h + i * 11) % 90));
    const restante = Math.max(0, total - failed - optOut);
    const sent = status === 'concluida' ? restante : status === 'em_andamento' ? proporcao(restante, 4_600) : status === 'pausada' ? proporcao(restante, 3_100) : 0;
    const delivered = sent - proporcao(sent, 380 + ((h + i) % 200));
    const read = proporcao(delivered, 5_400 + ((h + i * 5) % 2_600));
    const temLink = modelo.buttons.some((b) => b.kind === 'link');
    const clicked = temLink ? proporcao(read, 900 + ((h * (i + 2)) % 1_300)) : 0;
    const replied = proporcao(delivered, (segmento?.family === 'comercial' ? 1_400 : 500) + ((h * (i + 1)) % 1_100));
    const b = segmento?.benchmark ?? null;
    const taxaBps = b === null || b.sent === 0 ? 250 : Math.round((b.paid * 10_000) / b.sent);
    const janelaAberta = status !== 'concluida' || minutosAtras < JANELA_ATRIBUICAO_DIAS * MINUTOS_POR_DIA;
    // Periodo anterior com a regua de retomada menos afinada (65% da taxa de hoje): da variacao real entre as empresas.
    const fatorBps = anterior ? 6_500 : janelaAberta ? 7_500 : 10_000;
    const paid = proporcao(delivered, proporcao(taxaBps, fatorBps));
    const paidViaSeller = Math.min(paid, proporcao(replied, 2_000 + (h % 2_500)));
    // Ticket medio da familia: o valor de cada pagamento nao pula entre ofertas de R$ 97 e R$ 897 de um periodo para o outro.
    const recovered = paid * ticketDaFamilia(empresa, segmento?.family ?? 'carrinho', null);
    const chave = segmento?.key ?? 'abandonaram_sem_pagar';
    const vendedora = vendedoras.length === 0 ? null : vendedoras[i % vendedoras.length] ?? null;

    return {
      id: `SIMULADO-envio-${empresa.slug}-${anterior ? 'a' : ''}${String(i + 1).padStart(3, '0')}`,
      companyId: empresa.id,
      name: viaCsv ? `Lista importada — ${diaMes(createdAt)}` : `${NOME_POR_SEGMENTO[chave]} — ${diaMes(createdAt)}`,
      senderKind,
      templateId: modelo.id,
      templateName: modelo.name,
      templateCategory: modelo.category,
      status,
      pausedReason: status === 'pausada' ? (pausadoPorOptOut ? 'optout_alto' : 'manual') : null,
      audience: viaCsv ? { source: 'csv', segments: [], fileName: `contatos-${empresa.slug}.csv` } : { source: 'segmentos', segments: [chave], fileName: null },
      createdAt,
      scheduledFor: status === 'agendada' ? new Date(AGORA_SIMULADO.getTime() + (1 + (h % 3)) * DIA_MS).toISOString() : null,
      startedAt: status === 'agendada' ? null : createdAt,
      finishedAt: status === 'concluida' ? menosMinutos(Math.max(1, minutosAtras - 40 - (h % 30))) : null,
      totalContacts: total,
      sentCount: sent,
      deliveredCount: delivered,
      readCount: read,
      clickedCount: clicked,
      repliedCount: replied,
      failedCount: failed,
      optOutCount: optOut,
      paidCount: paid,
      paidViaSellerCount: paidViaSeller,
      recoveredAmountCents: recovered,
      commissionCents: Math.round((recovered * fee) / 10_000),
      costPerMessageCents: tarifaPorMensagemCents(senderKind, modelo.category, TARIFAS_WHATSAPP),
      assignedSellerIds: i % 3 === 0 && vendedora !== null ? [vendedora.id] : [],
      perMinute: senderKind === 'oficial' ? 40 : 20,
      autoPauseOptOutBps: 300,
    };
  });
}

function totalizarPorCanal(companyId: string, envios: readonly Broadcast[]): readonly BroadcastTotals[] {
  const canais: readonly SenderKind[] = ['evolution', 'oficial'];
  return canais
    .map((senderKind) => {
      const doCanal = envios.filter((e) => e.senderKind === senderKind);
      return doCanal.reduce<BroadcastTotals>(
        (acc, e) => ({
          ...acc,
          broadcasts: acc.broadcasts + 1,
          sent: acc.sent + e.sentCount,
          delivered: acc.delivered + e.deliveredCount,
          replied: acc.replied + e.repliedCount,
          paid: acc.paid + e.paidCount,
          failed: acc.failed + e.failedCount,
          optOut: acc.optOut + e.optOutCount,
          recoveredAmountCents: acc.recoveredAmountCents + e.recoveredAmountCents,
          commissionCents: acc.commissionCents + e.commissionCents,
          costCents: acc.costCents + e.sentCount * e.costPerMessageCents,
        }),
        { companyId, senderKind, broadcasts: 0, sent: 0, delivered: 0, replied: 0, paid: 0, failed: 0, optOut: 0, recoveredAmountCents: 0, commissionCents: 0, costCents: 0 },
      );
    })
    .filter((t) => t.broadcasts > 0);
}

function oportunidadesDaEmpresa(empresa: CompanySummary, envios: readonly Broadcast[]): readonly Opportunity[] {
  const prontos = remetentesDaEmpresa(empresa).some((s) => s.connected);
  return segmentosDaEmpresa(empresa)
    .filter((s) => s.estimatedCount > 0)
    .map((s) => {
      const ultimos = envios
        .filter((e) => e.status !== 'agendada' && e.audience.segments.includes(s.key))
        .map((e) => e.createdAt)
        .sort();
      const b = s.benchmark;
      return {
        companyId: empresa.id,
        segmentKey: s.key,
        family: s.family,
        contacts: s.estimatedCount,
        stalledAmountCents: s.stalledAmountCents,
        expectedRecoveredCents: b === null || b.sent === 0 ? null : Math.round((s.estimatedCount * b.recoveredAmountCents) / b.sent),
        lastBroadcastAt: ultimos[ultimos.length - 1] ?? null,
        senderReady: prontos,
      };
    });
}

function ordenarPorCriacaoDesc<T extends { readonly createdAt: string }>(lista: readonly T[]): readonly T[] {
  return [...lista].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

export function simularDisparos(companyId: string | null, range: PeriodRange): BroadcastsData {
  const dias = diasDaJanela(range);
  const anterior = janelaAnterior(range);
  const empresas: readonly CompanySummary[] = companyId === null ? EMPRESAS : [empresaPorId(companyId) ?? empresaFallback(companyId)];
  const porEmpresa = empresas.map((e) => ({ empresa: e, envios: enviosDaEmpresa(e, dias, 'atual') }));
  return {
    companyId,
    broadcasts: ordenarPorCriacaoDesc(porEmpresa.flatMap((p) => p.envios)),
    previous: anterior === null ? [] : empresas.flatMap((e) => totalizarPorCanal(e.id, enviosDaEmpresa(e, dias, 'anterior'))),
    previousPeriod: anterior,
    opportunities: porEmpresa.flatMap((p) => oportunidadesDaEmpresa(p.empresa, p.envios)),
    optOutTotal: empresas.reduce((acc, e) => acc + optOutsDaEmpresa(e).total, 0),
    periodStart: range.from ?? menosMinutos(DIAS_PADRAO * MINUTOS_POR_DIA),
    periodEnd: range.to ?? AGORA_SIMULADO.toISOString(),
  };
}

// -----------------------------------------------------------------------------
// Campanhas de e-mail (tambem so depois do fato)
// -----------------------------------------------------------------------------

interface CopyDeEmail {
  readonly subject: string;
  readonly preheader: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly segmentKey: SegmentKey;
}

const COPIES_DE_EMAIL: readonly CopyDeEmail[] = [
  {
    subject: '{primeiro_nome}, seu {produto} ficou parado no checkout',
    preheader: 'Está tudo salvo do jeito que você deixou',
    body: 'Oi, {primeiro_nome}!\nVi que você começou a finalizar o {produto} e o pagamento não chegou a ser concluído. Dá pra terminar em menos de um minuto, sem preencher nada de novo.\nSe preferir, copie este endereço: {link}',
    ctaLabel: 'Concluir compra',
    segmentKey: 'abandonaram_sem_pagar',
  },
  {
    subject: 'O pagamento do {produto} não foi concluído',
    preheader: 'Dá pra tentar de outro jeito em um minuto',
    body: 'Oi, {primeiro_nome}!\nO pagamento do {produto} não passou — o Pix venceu ou o cartão foi recusado. Nada se perdeu: o pedido continua salvo.\nTente com outra forma de pagamento: {link}',
    ctaLabel: 'Pagar de outro jeito',
    segmentKey: 'pagamento_recusado',
  },
  {
    subject: 'Seu acesso ao {produto} está parado há 30 dias',
    preheader: 'Seu progresso continua salvo',
    body: 'Oi, {primeiro_nome}!\nFaz 30 dias que o seu acesso venceu. Tudo o que você fez continua exatamente como deixou.\nRetome por aqui: {link}',
    ctaLabel: 'Reativar acesso',
    segmentKey: 'expirados_d30',
  },
  {
    subject: 'Ficou alguma dúvida sobre o {produto}?',
    preheader: 'A equipe comercial responde por aqui',
    body: 'Oi, {primeiro_nome}!\nA gente conversou sobre o {produto} e a compra não chegou a ser concluída. Se ficou alguma dúvida, é só responder este e-mail.\nSe já estiver decidido, termine por aqui: {link}',
    ctaLabel: 'Concluir compra',
    segmentKey: 'responderam_sem_fechar',
  },
  {
    subject: 'Último aviso sobre o seu acesso ao {produto}',
    preheader: 'Seu histórico continua salvo',
    body: 'Oi, {primeiro_nome}!\nFaz dois meses que o seu acesso venceu e este é o nosso último e-mail sobre isso. Se quiser voltar, tudo continua salvo.\n{link}',
    ctaLabel: 'Quero voltar',
    segmentKey: 'expirados_d60',
  },
  {
    subject: 'Você chegou até o pagamento do {produto}',
    preheader: 'Falta só um passo',
    body: 'Oi, {primeiro_nome}!\nVocê abriu o checkout do {produto} e parou no último passo. Se travou em alguma coisa, responda este e-mail que a gente ajuda.\n{link}',
    ctaLabel: 'Concluir agora',
    segmentKey: 'clicaram_sem_pagar',
  },
];

function statusDaCampanha(posicao: number): EmailCampaignStatus {
  if (posicao === 0) {
    return 'enviando';
  }
  if (posicao === 1 || posicao === 5) {
    return 'agendada';
  }
  if (posicao % 5 === 2) {
    return 'rascunho';
  }
  return 'concluida';
}

/** Distribui aberturas/cliques em ate 7 dias a partir da criacao, com pesos fixos. */
function serieDiaria(createdAt: string, opened: number, clicked: number, unsubscribed: number): readonly EmailDailyPoint[] {
  const pesos = [42, 24, 13, 9, 6, 4, 2];
  const inicio = new Date(createdAt);
  const somaPesos = pesos.reduce((a, b) => a + b, 0);
  return pesos.map((peso, i) => ({
    day: inicioDoDia(new Date(inicio.getTime() + i * DIA_MS).toISOString()),
    opened: Math.round((opened * peso) / somaPesos),
    clicked: Math.round((clicked * peso) / somaPesos),
    unsubscribed: Math.round((unsubscribed * peso) / somaPesos),
  }));
}

function gerarCampanhas(dias: number, anterior: boolean): readonly EmailCampaign[] {
  const ativas = EMPRESAS.filter((e) => e.status !== 'onboarding');
  const quantidade = limitar(Math.round(((CAMPANHAS_EMAIL_BASE_30D - (anterior ? 1 : 0)) * dias) / DIAS_PADRAO), anterior ? 1 : 2, MAXIMO_CAMPANHAS);
  const deslocamento = anterior ? dias * MINUTOS_POR_DIA : 0;

  return Array.from({ length: quantidade }, (_, i): EmailCampaign => {
    const empresa = ativas[i % Math.max(1, ativas.length)] ?? EMPRESAS[0];
    if (empresa === undefined) {
      throw new Error('catalogo de empresas vazio');
    }
    const h = hashSimulado(`${empresa.id}:email:${i}${anterior ? ':anterior' : ''}`);
    // So copies cujo publico existe na empresa (sem expirados, nada de e-mail de D+30).
    const segmentos = segmentosDaEmpresa(empresa);
    const contagem = (k: SegmentKey): number => segmentos.find((sg) => sg.key === k)?.estimatedCount ?? 0;
    const disponiveis = COPIES_DE_EMAIL.filter((c) => contagem(c.segmentKey) > 0);
    const copy = disponiveis[(h + i) % Math.max(1, disponiveis.length)] ?? COPIES_DE_EMAIL[0];
    if (copy === undefined) {
      throw new Error('copies de e-mail vazias');
    }
    const oferta = ofertaCiclica(empresa.id, i);
    const status = anterior ? 'concluida' : statusDaCampanha(i);
    const minutosAtras = deslocamento + Math.round(((i + 0.5) * dias * MINUTOS_POR_DIA) / quantidade) + (h % 120);
    const createdAt = menosMinutos(minutosAtras);
    // A campanha e do tamanho do publico pos-fato (80% a 100% dele, sem descadastrados).
    const total = status === 'rascunho' ? 0 : proporcao(contagem(copy.segmentKey), 8_000 + ((h * (i + 2)) % 2_000));
    const failed = status === 'concluida' || status === 'enviando' ? proporcao(total, 120 + ((h + i * 17) % 300)) : 0;
    const accepted = status === 'concluida' ? total - failed : status === 'enviando' ? proporcao(total - failed, 5_500) : 0;
    const opened = proporcao(accepted, 2_800 + ((h * 3) % 1_800));
    const clicked = proporcao(opened, 1_800 + ((h * 5) % 1_500));
    const unsubscribed = proporcao(accepted, 40 + ((h + i) % 70));
    const paid = proporcao(clicked, 400 + ((h * 7) % 600));
    const recovered = paid * oferta.priceCents;
    // A segunda agendada (posicao 5) ja passou da hora: e o "agendado vencido" da barra de status.
    const scheduledFor = status === 'agendada' ? new Date(AGORA_SIMULADO.getTime() + (i === 5 ? -(3 + (h % 5)) : 1 + (h % 3)) * 3_600_000).toISOString() : null;

    return {
      id: `SIMULADO-email-${empresa.slug}-${anterior ? 'a' : ''}${String(i + 1).padStart(3, '0')}`,
      companyId: empresa.id,
      subject: copy.subject.replace('{produto}', oferta.name),
      preheader: copy.preheader,
      body: copy.body.replace(/\{produto\}/g, oferta.name),
      ctaLabel: copy.ctaLabel,
      ctaUrl: `https://${empresa.slug}.exemplo/checkout/${oferta.id.toLowerCase()}`,
      segmentKey: copy.segmentKey,
      status,
      createdAt,
      scheduledFor,
      totalRecipients: total,
      acceptedCount: accepted,
      failedCount: failed,
      openedCount: opened,
      clickedCount: clicked,
      unsubscribedCount: unsubscribed,
      paidCount: paid,
      recoveredAmountCents: recovered,
      commissionCents: Math.round((recovered * comissaoBps(empresa)) / 10_000),
      daily: serieDiaria(createdAt, opened, clicked, unsubscribed),
    };
  });
}

function totalizarEmails(campanhas: readonly EmailCampaign[]): EmailTotals {
  return campanhas.reduce<EmailTotals>(
    (acc, c) => ({
      campaigns: acc.campaigns + 1,
      accepted: acc.accepted + c.acceptedCount,
      failed: acc.failed + c.failedCount,
      opened: acc.opened + c.openedCount,
      clicked: acc.clicked + c.clickedCount,
      unsubscribed: acc.unsubscribed + c.unsubscribedCount,
      paid: acc.paid + c.paidCount,
      recoveredAmountCents: acc.recoveredAmountCents + c.recoveredAmountCents,
      commissionCents: acc.commissionCents + c.commissionCents,
    }),
    { campaigns: 0, accepted: 0, failed: 0, opened: 0, clicked: 0, unsubscribed: 0, paid: 0, recoveredAmountCents: 0, commissionCents: 0 },
  );
}

const DNS_POR_EMPRESA: Readonly<Record<string, Omit<EmailDomainHealth, 'companyId' | 'domain'>>> = {
  'SIMULADO-empresa-demo': { spf: 'ok', dkim: 'ok', dmarc: 'aviso', dmarcPolicy: 'none' },
  'SIMULADO-empresa-fit': { spf: 'ok', dkim: 'ok', dmarc: 'ok', dmarcPolicy: 'quarantine' },
  'SIMULADO-empresa-dev': { spf: 'ok', dkim: 'aviso', dmarc: 'aviso', dmarcPolicy: 'none' },
  'SIMULADO-empresa-bem': { spf: 'falha', dkim: 'falha', dmarc: 'falha', dmarcPolicy: null },
};

function dominiosDasEmpresas(): readonly EmailDomainHealth[] {
  return EMPRESAS.map((e) => ({
    companyId: e.id,
    domain: `${e.slug}.exemplo`,
    ...(DNS_POR_EMPRESA[e.id] ?? { spf: 'aviso', dkim: 'aviso', dmarc: 'aviso', dmarcPolicy: null }),
  }));
}

export function simularCampanhasEmail(range: PeriodRange): EmailCampaignsData {
  const dias = diasDaJanela(range);
  const anterior = janelaAnterior(range);
  const campaigns = gerarCampanhas(dias, false);
  const concluidas = campaigns.filter((c) => c.status === 'concluida');
  const ultimaConcluida = ordenarPorCriacaoDesc(concluidas)[0];

  return {
    providerState: 'a_definir',
    lastSentAt: ultimaConcluida === undefined ? null : ultimaConcluida.createdAt,
    overdueScheduled: campaigns.filter((c) => c.status === 'agendada' && c.scheduledFor !== null && c.scheduledFor < AGORA_SIMULADO.toISOString()).length,
    sendingNow: campaigns.filter((c) => c.status === 'enviando').length,
    campaigns: ordenarPorCriacaoDesc(campaigns),
    automations: AUTOMACOES_EMAIL,
    unsubscribes: DESCADASTRADOS,
    unsubscribeTotal: campaigns.reduce((acc, c) => acc + c.unsubscribedCount, 0),
    domains: dominiosDasEmpresas(),
    perThousandCents: TARIFA_EMAIL_POR_MIL_CENTS,
    previous: anterior === null ? null : totalizarEmails(gerarCampanhas(dias, true)),
    previousPeriod: anterior,
    periodStart: range.from ?? menosMinutos(DIAS_PADRAO * MINUTOS_POR_DIA),
    periodEnd: range.to ?? AGORA_SIMULADO.toISOString(),
  };
}

/**
 * Espelha os toques com e-mail da regua padrao (T+1h, T+24h, T+48h no carrinho;
 * D+15..D+90 nos expirados) sem importar do modulo de funis: os modulos nascem
 * em paralelo e um import cruzado quebraria um quando o outro mudasse.
 */
export const AUTOMACOES_EMAIL: readonly EmailAutomation[] = [
  { id: 'SIMULADO-automacao-T1h', funnel: 'carrinho_abandonado', label: 'E-mail do T+1h', description: 'Sai junto com o WhatsApp, 1 hora depois do abandono.', enabled: true },
  { id: 'SIMULADO-automacao-T24h', funnel: 'carrinho_abandonado', label: 'E-mail do T+24h', description: 'Segundo lembrete, um dia depois, se ainda não pagou.', enabled: true },
  { id: 'SIMULADO-automacao-T48h', funnel: 'carrinho_abandonado', label: 'E-mail do T+48h', description: 'Último aviso do carrinho; encerra a régua.', enabled: true },
  { id: 'SIMULADO-automacao-D15', funnel: 'expirados', label: 'E-mail do D+15', description: 'Assinatura vencida há 15 dias: "seu progresso continua salvo".', enabled: true },
  { id: 'SIMULADO-automacao-D30', funnel: 'expirados', label: 'E-mail do D+30', description: 'Convite para retomar de onde parou.', enabled: true },
  { id: 'SIMULADO-automacao-D60', funnel: 'expirados', label: 'E-mail do D+60', description: 'Dois meses depois do vencimento, sem pressão.', enabled: false },
  { id: 'SIMULADO-automacao-D90', funnel: 'expirados', label: 'E-mail do D+90', description: 'Último contato da régua de expirados.', enabled: false },
];

const DESCADASTRADOS: readonly EmailUnsubscribe[] = [
  { id: 'SIMULADO-descadastro-001', companyId: 'SIMULADO-empresa-demo', email: 'mariana.ferraz@exemplo.exemplo', campaignSubject: 'Seu acesso está parado há 30 dias', at: menosMinutos(95) },
  { id: 'SIMULADO-descadastro-002', companyId: 'SIMULADO-empresa-fit', email: 'joao.lemos@exemplo.exemplo', campaignSubject: 'O pagamento não foi concluído', at: menosMinutos(6 * 60 + 12) },
  { id: 'SIMULADO-descadastro-003', companyId: 'SIMULADO-empresa-demo', email: 'aline.prado@exemplo.exemplo', campaignSubject: 'Último aviso sobre o seu acesso', at: menosMinutos(MINUTOS_POR_DIA + 40) },
  { id: 'SIMULADO-descadastro-004', companyId: 'SIMULADO-empresa-dev', email: 'ricardo.nunes@exemplo.exemplo', campaignSubject: 'Seu produto ficou parado no checkout', at: menosMinutos(2 * MINUTOS_POR_DIA + 300) },
  { id: 'SIMULADO-descadastro-005', companyId: 'SIMULADO-empresa-fit', email: 'beatriz.camargo@exemplo.exemplo', campaignSubject: 'Ficou alguma dúvida?', at: menosMinutos(3 * MINUTOS_POR_DIA + 15) },
  { id: 'SIMULADO-descadastro-006', companyId: 'SIMULADO-empresa-demo', email: 'thiago.moura@exemplo.exemplo', campaignSubject: 'Você chegou até o pagamento', at: menosMinutos(4 * MINUTOS_POR_DIA + 600) },
  { id: 'SIMULADO-descadastro-007', companyId: 'SIMULADO-empresa-dev', email: 'renata.dias@exemplo.exemplo', campaignSubject: 'Seu acesso está parado há 30 dias', at: menosMinutos(6 * MINUTOS_POR_DIA + 90) },
  { id: 'SIMULADO-descadastro-008', companyId: 'SIMULADO-empresa-fit', email: 'paulo.souza@exemplo.exemplo', campaignSubject: 'Último aviso sobre o seu acesso', at: menosMinutos(8 * MINUTOS_POR_DIA + 25) },
].slice(0, AMOSTRA_DESCADASTRO);
